/**
 * Time-Left Hook
 *
 * Adaptive-cadence view of `formatTimeLeftValue` for the card surface.
 * Returns the discriminated `TimeLeft` (days vs HH:MM:SS) or null if the
 * market has expired or no `expiresAt` was provided. Ticks every second
 * inside the final 24h, and only at day-boundary crossings outside it.
 * Stops scheduling once the market expires.
 */

import { useEffect, useState } from "preact/hooks";
import { formatTimeLeftValue, type TimeLeft } from "../utils/formatters";

const oneDayMs = 24 * 60 * 60 * 1000;

export function useTimeLeft(expiresAt: string | undefined): TimeLeft | null {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(() =>
    formatTimeLeftValue(expiresAt),
  );

  useEffect((): (() => void) | void => {
    const initial = formatTimeLeftValue(expiresAt);
    setTimeLeft(initial);
    if (!expiresAt || !initial) return;

    let timer: ReturnType<typeof setTimeout>;
    const tick = (current: TimeLeft): void => {
      const delay =
        current.kind === "hms"
          ? 1000
          : // Time until `floor(diff / oneDay)` decrements by one. Floored
            // at 250ms to absorb timer drift around the day boundary.
            Math.max(
              (new Date(expiresAt).getTime() - Date.now()) % oneDayMs,
              250,
            );
      timer = setTimeout((): void => {
        const next = formatTimeLeftValue(expiresAt);
        setTimeLeft(next);
        if (next) tick(next);
      }, delay);
    };
    tick(initial);

    return (): void => clearTimeout(timer);
  }, [expiresAt]);

  return timeLeft;
}
